import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { FiArrowDownLeft, FiArrowUpRight, FiCreditCard } from "react-icons/fi";
import { getWalletHistory } from "./WalletHistory.api";
import { useAuth } from "context/auth-context";
import { dateFormatHandler, DD_MMM_YYYY } from "../../lib/date-formatter-moment";
import PaginationNew from "components/Pagination/PaginationNew";
import LoadingOverlay from "react-loading-overlay-ts";
import { Badge, Card, EmptyState, PageHeader, SkeletonRows } from "components/ui";

const TypeBadge = ({ isCredit, label }: { isCredit: boolean; label: React.ReactNode }) => (
  <Badge tone={isCredit ? "success" : "neutral"} className="capitalize">
    {isCredit ? <FiArrowDownLeft /> : <FiArrowUpRight />}
    {label}
  </Badge>
);

export const WalletHistory = () => {
  const { t } = useTranslation();
  const { auth } = useAuth();
  const [currentPage, setCurrentPage] = useState<number>(1);

  const getHistoryData = useQuery(["wallethistory", currentPage], () => getWalletHistory(auth.panelistId, currentPage), {
    refetchOnWindowFocus: false,
  });

  const historyList = getHistoryData?.data?.result?.historyList || [];
  const rows = historyList.map((survey: any, i: number) => {
    const isCredit = survey?.type?.toLowerCase() === "credit";
    return {
      survey,
      isCredit,
      serialNumber: (currentPage - 1) * 10 + i + 1,
      pointsColor: isCredit ? "text-success" : "text-danger",
      formattedPoints: `${isCredit ? "+" : "-"}${Math.floor(+survey.points)}`,
      date: dateFormatHandler(survey?.created_at, DD_MMM_YYYY),
    };
  });

  const emptyState = getHistoryData?.isLoading ? (
    <SkeletonRows rows={6} />
  ) : (
    <EmptyState
      icon={<FiCreditCard />}
      title={t("page.mysurvey.nodata") || "No Data Found"}
      description="Points you earn and redeem will be listed here."
    />
  );

  return (
    <LoadingOverlay
      styles={{
        overlay: (base) => ({
          ...base,
          position: "fixed",
          background: "rgb(var(--zs-overlay) / 0.45)",
        }),
      }}
      active={getHistoryData?.isLoading || getHistoryData?.isFetching}
      spinner
      text={t("claim.card.Loading")}
    >
      <div className="zs-page zs-page-medium">
        <PageHeader
          eyebrow="Rewards"
          title="Wallet Transaction History"
          subtitle="Every credit and debit to your points wallet."
        />

        <Card flush>
          {/* Mobile: stacked transaction list */}
          <ul className="divide-y divide-line sm:hidden">
            {rows.length > 0
              ? rows.map(({ survey, isCredit, serialNumber, pointsColor, formattedPoints, date }, i: number) => (
                  <li key={i} className="flex items-start gap-3 px-4 py-3.5">
                    <span
                      className={`zs-stat-icon !w-9 !h-9 ${
                        isCredit ? "!bg-success-soft !text-success" : "!bg-surface-hover !text-ink-3"
                      }`}
                    >
                      {isCredit ? <FiArrowDownLeft /> : <FiArrowUpRight />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium break-words text-ink">{survey.description}</p>
                      <p className="mt-0.5 text-xs text-ink-3">
                        #{serialNumber} · {date} · <span className="capitalize">{survey.type}</span>
                      </p>
                    </div>
                    <span className={`text-sm font-semibold tabular-nums whitespace-nowrap ${pointsColor}`}>
                      {formattedPoints}
                    </span>
                  </li>
                ))
              : null}
          </ul>

          {/* Tablet and up: data table */}
          <div className="hidden zs-table-wrap is-flush sm:block">
            <table className="zs-table">
              <thead>
                <tr>
                  <th className="w-16">{t("pages.referral.SrNo")}</th>
                  <th>Description</th>
                  <th className="is-num">{t("page.history.Points")}</th>
                  <th>Type</th>
                  <th>Transaction Date</th>
                </tr>
              </thead>
              <tbody data-testid="survey1">
                {rows.length > 0 ? (
                  rows.map(({ survey, isCredit, serialNumber, pointsColor, formattedPoints, date }, i: number) => (
                    <tr key={i}>
                      <td className="tabular-nums text-ink-3">{serialNumber}</td>
                      <td className="is-strong">{survey.description}</td>
                      <td className={`is-num font-semibold ${pointsColor}`}>{formattedPoints}</td>
                      <td>
                        <TypeBadge isCredit={isCredit} label={survey.type} />
                      </td>
                      <td className="whitespace-nowrap text-ink-3">{date}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="!p-0">
                      {emptyState}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {rows.length === 0 && <div className="sm:hidden">{emptyState}</div>}

          {historyList.length > 0 && (
            <div className="zs-table-footer">
              <span>
                Page {currentPage} of {getHistoryData?.data?.result?.totalPages}
              </span>
              <PaginationNew
                setCurrentPage={setCurrentPage}
                totalPages={getHistoryData?.data?.result?.totalPages}
                currentPage={currentPage}
              />
            </div>
          )}
        </Card>
      </div>
    </LoadingOverlay>
  );
};
